<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import { Pencil, Trash2 } from "lucide-vue-next";
  import { useAnnouncementsStore } from "@stores/announcements";
  import { useToastsStore } from "@stores/toastsStore";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import {
    DataTable,
    DataTableActions,
    type DataTableColumn,
    type DataTableAction,
  } from "@/components/episto-ui/data-table";
  import { Badge } from "@/components/ui/badge";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import { serverErrorDetail } from "@utils/serverErrorDetail";
  import AnnouncementFormDialog, { type AnnouncementForm } from "./AnnouncementFormDialog.vue";
  import type { Announcement, AnnouncementStatus } from "@/types";

  const STATUS_BADGES: Record<AnnouncementStatus, { label: string; variant: "success" | "info" | "secondary" }> = {
    live: { label: "Live", variant: "success" },
    scheduled: { label: "Scheduled", variant: "info" },
    ended: { label: "Ended", variant: "secondary" },
    inactive: { label: "Inactive", variant: "secondary" },
  };

  const announcementsStore = useAnnouncementsStore();
  const toast = useToastsStore();
  const { announcements } = storeToRefs(announcementsStore);

  const searchQuery = ref("");

  const filteredAnnouncements = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return announcements.value;
    return announcements.value.filter(
      (announcement) =>
        announcement.title.toLowerCase().includes(query) || announcement.url.toLowerCase().includes(query),
    );
  });

  const formatDate = (date: string | null) => (date ? moment(date).format("D MMM YYYY") : "—");

  const emptyForm = (): AnnouncementForm => ({
    title: "",
    body: "",
    url: "",
    starts_on: moment.utc().format("YYYY-MM-DD"),
    ends_on: null,
    active: true,
  });

  const isDialogOpen = ref(false);
  const isEditing = ref(false);
  const currentAnnouncement = ref<AnnouncementForm>(emptyForm());

  onMounted(() => announcementsStore.fetchAnnouncements());

  const columns: DataTableColumn<Announcement>[] = [
    { key: "title", label: "Title", class: "font-medium" },
    { key: "url", label: "Link", class: "font-mono text-xs max-w-xs truncate" },
    { key: "starts_on", label: "Start" },
    { key: "ends_on", label: "End" },
    { key: "status", label: "Status" },
  ];

  const tableActions: DataTableAction[] = [
    { key: "edit", label: "Edit", icon: Pencil },
    { key: "delete", label: "Delete", icon: Trash2, variant: "destructive", separatorBefore: true },
  ];

  const openCreateDialog = () => {
    isEditing.value = false;
    currentAnnouncement.value = emptyForm();
    isDialogOpen.value = true;
  };

  const openEditDialog = (announcement: Announcement) => {
    isEditing.value = true;
    currentAnnouncement.value = {
      id: announcement.id,
      title: announcement.title,
      body: announcement.body ?? "",
      url: announcement.url,
      starts_on: announcement.starts_on,
      ends_on: announcement.ends_on,
      active: announcement.active,
    };
    isDialogOpen.value = true;
  };

  const saveAnnouncement = async () => {
    try {
      const { id, ...body } = currentAnnouncement.value;

      if (isEditing.value && id) {
        await announcementsStore.updateAnnouncement(id, body);
        toast.success("Announcement updated successfully");
      } else {
        await announcementsStore.createAnnouncement(body);
        toast.success("Announcement created successfully");
      }
      await announcementsStore.fetchAnnouncements();
      isDialogOpen.value = false;
    } catch (error) {
      toast.error("Failed to save the announcement", serverErrorDetail(error));
    }
  };

  const {
    isOpen: isDeleteAlertOpen,
    target: announcementToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<Announcement>(async (announcement) => {
    try {
      await announcementsStore.deleteAnnouncement(announcement.id);
      toast.success("Announcement deleted successfully");
    } catch {
      toast.error("Failed to delete announcement");
    }
  });

  const handleAction = (action: string, announcement: Announcement) => {
    if (action === "edit") {
      openEditDialog(announcement);
    } else if (action === "delete") {
      confirmDelete(announcement);
    }
  };
</script>

<template>
  <IndexPageLayout
    title="Announcements"
    action-label="Add Announcement"
    @action="openCreateDialog">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search announcements..." />
    </template>

    <DataTable
      :items="filteredAnnouncements"
      :columns="columns"
      empty-message="No announcements found.">
      <template #cell-starts_on="{ item }">
        {{ formatDate(item.starts_on) }}
      </template>
      <template #cell-ends_on="{ item }">
        {{ formatDate(item.ends_on) }}
      </template>
      <template #cell-status="{ item }">
        <Badge :variant="STATUS_BADGES[item.status].variant">
          {{ STATUS_BADGES[item.status].label }}
        </Badge>
      </template>
      <template #actions="{ item }">
        <DataTableActions
          :actions="tableActions"
          @action="(action) => handleAction(action, item)" />
      </template>
    </DataTable>

    <template #dialogs>
      <AnnouncementFormDialog
        v-model:announcement="currentAnnouncement"
        :open="isDialogOpen"
        :is-editing="isEditing"
        @update:open="isDialogOpen = $event"
        @save="saveAnnouncement" />

      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Are you absolutely sure?"
        confirm-label="Delete"
        @confirm="handleDelete">
        This action cannot be undone. This will permanently delete the announcement
        <span class="font-bold">{{ announcementToDelete?.title }}</span>
        and the list of users who dismissed it.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
